import { useEffect, useState } from "react";
import { Link, useLocation, NavLink } from "react-router-dom";
import { Menu, X, Phone, ArrowUpRight, ChevronDown, Plus, Trophy } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { NAV_MENU, BUSINESS } from "../data/content";
import { BrandMark } from "./BrandMark";

const DesktopItem = ({ item }) => {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const active = item.to && (item.to === "/" ? pathname === "/" : pathname.startsWith(item.to));

  if (!item.children) {
    return (
      <NavLink
        to={item.to}
        data-testid={`nav-link-${item.label.toLowerCase()}`}
        className={({ isActive }) =>
          `rounded-full px-4 py-2 text-sm font-semibold transition-colors duration-200 ${
            isActive ? "text-brand-dark" : "text-ink/70 hover:text-ink"
          }`
        }
      >
        {item.label}
      </NavLink>
    );
  }

  return (
    <div
      className="relative"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <Link
        to={item.to || "#"}
        data-testid={`nav-link-${item.label.toLowerCase()}`}
        className={`flex items-center gap-1 rounded-full px-4 py-2 text-sm font-semibold transition-colors duration-200 ${
          active ? "text-brand-dark" : "text-ink/70 hover:text-ink"
        }`}
      >
        {item.label}
        <ChevronDown
          className={`h-3.5 w-3.5 transition-transform duration-200 ${open ? "rotate-180" : ""}`}
        />
      </Link>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 8 }}
            transition={{ duration: 0.18 }}
            data-testid={`nav-dropdown-${item.label.toLowerCase()}`}
            className="absolute left-1/2 top-full z-50 w-[320px] -translate-x-1/2 pt-3"
          >
            <div className="overflow-hidden rounded-2xl border border-black/10 bg-white p-2 shadow-2xl shadow-ink/10">
              {item.children.map((c) => (
                <Link
                  key={c.to}
                  to={c.to}
                  data-testid={`nav-sub-${c.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  className="group/sub flex items-start gap-3 rounded-xl p-3 transition-colors hover:bg-paper"
                >
                  {c.icon && (
                    <span className="mt-0.5 grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-brand/10 text-brand-dark transition-colors group-hover/sub:bg-brand group-hover/sub:text-ink">
                      <c.icon className="h-4 w-4" />
                    </span>
                  )}
                  <span>
                    <span className="block text-sm font-bold text-ink">{c.label}</span>
                    {c.desc && (
                      <span className="block text-xs text-ink/50">{c.desc}</span>
                    )}
                  </span>
                </Link>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const MobileItem = ({ item, close }) => {
  const [open, setOpen] = useState(false);
  if (!item.children) {
    return (
      <Link
        to={item.to}
        onClick={close}
        data-testid={`mobile-nav-${item.label.toLowerCase()}`}
        className="block border-b border-black/5 py-4 font-display text-3xl font-bold uppercase tracking-tight text-ink"
      >
        {item.label}
      </Link>
    );
  }
  return (
    <div className="border-b border-black/5">
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid={`mobile-nav-${item.label.toLowerCase()}`}
        className="flex w-full items-center justify-between py-4 font-display text-3xl font-bold uppercase tracking-tight text-ink"
      >
        {item.label}
        <Plus className={`h-6 w-6 transition-transform duration-200 ${open ? "rotate-45" : ""}`} />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden"
          >
            <div className="pb-4 pl-1">
              {item.children.map((c) => (
                <Link
                  key={c.to}
                  to={c.to}
                  onClick={close}
                  data-testid={`mobile-sub-${c.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  className="block py-2.5 text-lg font-semibold text-ink/70"
                >
                  {c.label}
                </Link>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  return (
    <>
      <header
        data-testid="main-navbar"
        className={`fixed inset-x-0 top-0 z-50 border-b bg-white/80 backdrop-blur-2xl transition-shadow duration-300 ${
          scrolled ? "border-black/5 shadow-sm shadow-black/5" : "border-black/5"
        }`}
      >
        <div className="bg-ink text-white">
          <div className="mx-auto flex h-9 max-w-[1500px] items-center justify-center gap-2.5 px-5 sm:px-8">
            <Trophy className="h-3.5 w-3.5 text-brand" />
            <span className="font-mono text-[11px] font-bold uppercase tracking-[0.28em] text-white/90">
              Websites Built to Win
            </span>
          </div>
        </div>
        <div className="mx-auto flex h-[68px] max-w-[1500px] items-center gap-6 px-5 sm:px-8">
          <BrandMark />

          <nav className="hidden flex-1 items-center justify-evenly px-4 lg:flex xl:px-10">
            {NAV_MENU.map((item) => (
              <DesktopItem key={item.label} item={item} />
            ))}
          </nav>

          <div className="ml-auto hidden items-center gap-3 lg:flex">
            <a
              href={BUSINESS.phoneHref}
              data-testid="nav-phone"
              className="flex items-center gap-2 text-sm font-bold text-ink transition-colors hover:text-brand-dark"
            >
              <Phone className="h-4 w-4" />
              {BUSINESS.phone}
            </a>
            <Link
              to="/contact"
              data-testid="nav-quote-btn"
              className="group inline-flex items-center gap-1.5 rounded-full bg-brand px-5 py-2.5 text-sm font-bold text-ink transition-transform duration-300 hover:-translate-y-0.5 hover:bg-brand-dark hover:text-white"
            >
              Get a Quote
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </div>

          <button
            onClick={() => setOpen((v) => !v)}
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
            className="ml-auto grid h-10 w-10 place-items-center rounded-full border border-black/10 lg:hidden"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            data-testid="mobile-menu"
            className="fixed inset-0 z-40 overflow-y-auto bg-white px-5 pb-12 pt-24 lg:hidden"
          >
            <div className="flex flex-col">
              {NAV_MENU.map((item) => (
                <MobileItem key={item.label} item={item} close={() => setOpen(false)} />
              ))}
            </div>
            <div className="mt-8 flex flex-col gap-3">
              <a
                href={BUSINESS.phoneHref}
                className="flex items-center justify-center gap-2 rounded-full border border-black/10 py-3.5 font-bold"
              >
                <Phone className="h-4 w-4" /> {BUSINESS.phone}
              </a>
              <Link
                to="/contact"
                onClick={() => setOpen(false)}
                className="flex items-center justify-center gap-2 rounded-full bg-brand py-3.5 font-bold text-ink"
              >
                Get a Free Quote <ArrowUpRight className="h-4 w-4" />
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
